/**
 * The count at the end of a round, played out card by card.
 *
 * The server scores a round the moment someone goes out, and the view that
 * says so already holds every card that counted: the books and melds on the
 * table and - now the round is over - every Hand and Foot. This lays those
 * cards out in the order the table counts them, so the scores can be seen to
 * climb, and fall for anyone caught holding threes, rather than jump.
 *
 * Starting with the dealer's team, each stage goes round every team before
 * the next begins: book bonuses, the cards in the books, the cards in the
 * melds, and last whatever is still held.
 *
 * The pacing is kept in step with `getTallyMs` on the server, which holds the
 * next deal back for as long as the count takes. The browser fits the count
 * into whatever window it is given, so the two drifting apart only changes
 * the speed, never what is counted.
 */

import {
    type Card,
    type GameView,
    type Meld,
    type MeldableRank,
    type PlayerView,
    type Team,
    PURE_BOOK_BONUS,
    WILD_BOOK_BONUS,
    getCardValue,
    getPenaltyValue,
    isBook,
    isPure,
    isThree
} from "@ryzun/haf-saloon-game-engine";
import { selectMelds } from "./meld-text.js";
import { getRoundTotal } from "./table-text.js";

/** A moment to take in who went out before anything moves. */
export const TALLY_LEAD_MS = 1200;

/** A breath between one team's stage and the next. */
export const GROUP_PAUSE_MS = 350;

/** A book's bonus, counted before any of its cards move. */
export const BONUS_BEAT_MS = 500;

/** The gap between one card and the next: quick enough that only the total registers. */
export const CARD_BEAT_MS = 45;

/** The gap a three still held is given, so nobody misses it. */
export const THREE_BEAT_MS = 1000;

/** How long an ordinary card takes to reach the discard pile. */
export const TALLY_FLIGHT_MS = 260;

/** A three crosses slowly, swelling on the way, so it reads as the penalty it is. */
export const THREE_FLIGHT_MS = 800;

/** Room for the last card to land; the slowest flight, so a closing three fits. */
export const TALLY_TAIL_MS = THREE_FLIGHT_MS;

export type TallyStage = "bonus" | "books" | "held" | "melds";

export type HeldPile = "foot" | "hand";

/** Who was still holding a card when the round ended, and in which pile. */
export interface HeldBy {
    readonly playerId: string;
    readonly pile: HeldPile;
}

export interface TallyStep {
    readonly teamId: string;
    readonly stage: TallyStage;
    /** When the step starts, from the start of the count. */
    readonly delayMs: number;
    /** How long its card takes to reach the discard pile; zero for a bonus. */
    readonly flightMs: number;
    /** Signed: a bonus or a card on the table adds, a card still held takes away. */
    readonly points: number;
    /** The book or meld the card leaves, or the book whose bonus is counted; null for a held card. */
    readonly rank: MeldableRank | null;
    /** The card that moves; null for a bonus, which moves nothing. */
    readonly card: Card | null;
    readonly heldBy: HeldBy | null;
}

export interface TallyPlan {
    readonly steps: readonly TallyStep[];
    /** When the last card has landed and the summary can take over. */
    readonly durationMs: number;
}

/** How far the count has got. */
export interface TallyProgress {
    /** When each book's bonus was counted, on the `performance.now()` clock, by `getBookKey`. */
    readonly bonuses: ReadonlyMap<string, number>;
    /** Cards that have left the table or a hand, whether still in the air or on the pile. */
    readonly lifted: ReadonlySet<string>;
    /** Cards set down on the discard pile, in the order they landed. */
    readonly piled: readonly Card[];
    /** Points counted so far this round, by team. */
    readonly points: ReadonlyMap<string, number>;
}

/** A book as the count leaves it. */
export interface BookTally {
    /** When its bonus was counted, on the `performance.now()` clock; null until it is. */
    readonly scoredAt: number | null;
    /** Its cards not yet sent to the pile. */
    readonly left: number;
}

type Draft = Omit<TallyStep, "delayMs" | "flightMs">;

interface HeldCard {
    readonly card: Card;
    readonly pile: HeldPile;
}

const STAGES: readonly TallyStage[] = ["bonus", "books", "melds", "held"];

const STAGE_WORDS: Readonly<Record<TallyStage, string>> = {
    bonus: "Book bonuses",
    books: "Cards in books",
    held: "Cards still held",
    melds: "Cards in melds"
};

export const NOTHING_TALLIED: TallyProgress = {
    bonuses: new Map(),
    lifted: new Set(),
    piled: [],
    points: new Map()
};

export function getBookKey(teamId: string, rank: MeldableRank): string {
    return `${teamId}/${rank}`;
}

/** A three still in a hand when the round ended, which the count dwells on. */
export function isHeldThree(step: Pick<TallyStep, "card" | "stage">): boolean {
    return step.stage === "held" && step.card !== null && isThree(step.card);
}

/** Points as the scoreboard calls them out: always signed, with a true minus. */
export function formatPoints(points: number): string {
    if (points > 0) {
        return `+${points}`;
    }

    return points < 0 ? `−${Math.abs(points)}` : "0";
}

export function describeStage(stage: TallyStage): string {
    return STAGE_WORDS[stage];
}

/** What the table says while it waits to start counting. */
export function describeTallyStart(view: GameView): string {
    const wentOut = view.players.find((player) => player.id === view.wentOutPlayerId);

    return wentOut === undefined
        ? "The stock ran out. Counting the round…"
        : `${wentOut.name} went out. Counting the round…`;
}

export function describeHeldThree(name: string, card: Card): string {
    return `${name} was caught holding a ${card.color} 3 · ${formatPoints(-getPenaltyValue(card))}`;
}

/** The seats in turn from the dealer, who comes first. */
function getSeatOrder(view: GameView): readonly PlayerView[] {
    const seats = view.players.length;

    return view.players.map((_, step) => view.players[(view.dealerIndex + step) % seats]);
}

/** The dealer's team first, then each other team in the order its first seat comes round. */
function getTeamOrder(view: GameView, seats: readonly PlayerView[]): readonly Team[] {
    const seated = [...new Set(seats.map((player) => player.teamId))];
    const rank = (team: Team): number => {
        const index = seated.indexOf(team.id);

        return index < 0 ? seated.length : index;
    };

    return view.teams.toSorted((a, b) => rank(a) - rank(b));
}

/** Each card of a book or meld, last laid first, as it would come off the pile. */
function draftTableCards(team: Team, melds: readonly Meld[], stage: TallyStage): readonly Draft[] {
    return melds.flatMap((meld) => meld.cards.toReversed().map((card): Draft => ({
        teamId: team.id,
        stage,
        points: getCardValue(card),
        rank: meld.rank,
        card,
        heldBy: null
    })));
}

/** Each player's Hand then Foot, with any threes saved for last. */
function draftHeld(team: Team, seats: readonly PlayerView[]): readonly Draft[] {
    return seats.filter((player) => player.teamId === team.id).flatMap((player) => {
        const held = [
            ...(player.hand ?? []).map((card): HeldCard => ({ card, pile: "hand" })),
            ...(player.foot ?? []).map((card): HeldCard => ({ card, pile: "foot" }))
        ];
        const ordered = [
            ...held.filter((entry) => !isThree(entry.card)),
            ...held.filter((entry) => isThree(entry.card))
        ];

        return ordered.map(({ card, pile }): Draft => ({
            teamId: team.id,
            stage: "held",
            points: -getPenaltyValue(card),
            rank: null,
            card,
            heldBy: { playerId: player.id, pile }
        }));
    });
}

function draftStage(stage: TallyStage, team: Team, seats: readonly PlayerView[]): readonly Draft[] {
    switch (stage) {
        case "bonus":
            return selectMelds(team.melds, "books").map((book): Draft => ({
                teamId: team.id,
                stage,
                points: isPure(book) ? PURE_BOOK_BONUS : WILD_BOOK_BONUS,
                rank: book.rank,
                card: null,
                heldBy: null
            }));
        case "books":
        case "melds":
            return draftTableCards(team, selectMelds(team.melds, stage), stage);
        case "held":
            return draftHeld(team, seats);
    }
}

function getBeat(draft: Draft): number {
    if (draft.card === null) {
        return BONUS_BEAT_MS;
    }

    return isHeldThree(draft) ? THREE_BEAT_MS : CARD_BEAT_MS;
}

function getFlight(draft: Draft): number {
    if (draft.card === null) {
        return 0;
    }

    return isHeldThree(draft) ? THREE_FLIGHT_MS : TALLY_FLIGHT_MS;
}

/** The count at its own pace. */
function layOut(groups: readonly (readonly Draft[])[]): TallyPlan {
    const steps: TallyStep[] = [];
    let cursor = TALLY_LEAD_MS;

    for (const [index, group] of groups.entries()) {
        cursor += index > 0 ? GROUP_PAUSE_MS : 0;

        for (const draft of group) {
            steps.push({ ...draft, delayMs: cursor, flightMs: getFlight(draft) });
            cursor += getBeat(draft);
        }
    }

    return { steps, durationMs: cursor + TALLY_TAIL_MS };
}

/**
 * Every step of a finished round's count, in order. The count runs at its own
 * pace when the window allows, and is squeezed evenly to fit when it does not,
 * so a three always lingers longer than the cards around it.
 */
export function planTally(view: GameView, windowMs: number): TallyPlan {
    const seats = getSeatOrder(view);
    const teams = getTeamOrder(view, seats);
    const groups = STAGES
        .flatMap((stage) => teams.map((team) => draftStage(stage, team, seats)))
        .filter((group) => group.length > 0);
    const natural = layOut(groups);

    if (natural.durationMs <= windowMs) {
        return natural;
    }

    const scale = windowMs / natural.durationMs;

    return {
        steps: natural.steps.map((step) => ({
            ...step,
            delayMs: Math.round(step.delayMs * scale),
            flightMs: Math.round(step.flightMs * scale)
        })),
        durationMs: windowMs
    };
}

/** A step's card leaves where it was. */
export function liftCard(progress: TallyProgress, step: TallyStep): TallyProgress {
    if (step.card === null) {
        return progress;
    }

    return { ...progress, lifted: new Set([...progress.lifted, step.card.id]) };
}

/** A step's points go on the board: a bonus as it is called, a card as it lands on the pile. */
export function countStep(progress: TallyProgress, step: TallyStep, now: number): TallyProgress {
    const points = new Map([...progress.points, [step.teamId, (progress.points.get(step.teamId) ?? 0) + step.points]]);

    if (step.card !== null) {
        return { ...progress, points, piled: [...progress.piled, step.card] };
    }

    const bonuses = step.rank === null
        ? progress.bonuses
        : new Map([...progress.bonuses, [getBookKey(step.teamId, step.rank), now]]);

    return { ...progress, points, bonuses };
}

/** A team's score before this round was counted. */
export function getBankedScore(view: GameView, team: Team): number {
    return team.score - (getRoundTotal(view, view.round, team.id) ?? 0);
}

/**
 * A book keeps every card until the last has gone, or it would drop back into
 * the meld row partway through; its label counts down instead.
 */
function tallyMeld(meld: Meld, lifted: ReadonlySet<string>): readonly Meld[] {
    const left = meld.cards.filter((card) => !lifted.has(card.id));

    if (left.length === 0) {
        return [];
    }

    return [isBook(meld) ? meld : { ...meld, cards: left }];
}

function tallyPlayer(player: PlayerView, lifted: ReadonlySet<string>): PlayerView {
    const hand = player.hand?.filter((card) => !lifted.has(card.id)) ?? null;
    const foot = player.foot?.filter((card) => !lifted.has(card.id)) ?? null;

    return {
        ...player,
        hand,
        foot,
        handCount: hand?.length ?? player.handCount,
        footCount: foot?.length ?? player.footCount
    };
}

/**
 * The table as the count has left it: every card that has gone to the pile
 * taken off the felt and out of its hand, the pile topped by the last one to
 * land, and each team's score at what it banked before plus what has been
 * counted since.
 */
export function getTalliedView(view: GameView, progress: TallyProgress): GameView {
    const { lifted, piled } = progress;

    return {
        ...view,
        teams: view.teams.map((team) => ({
            ...team,
            melds: team.melds.flatMap((meld) => tallyMeld(meld, lifted)),
            score: getBankedScore(view, team) + (progress.points.get(team.id) ?? 0)
        })),
        players: view.players.map((player) => tallyPlayer(player, lifted)),
        discardTop: piled.at(-1) ?? view.discardTop,
        discardCount: view.discardCount + piled.length,
        discardFrozen: piled.length === 0 && view.discardFrozen
    };
}

/** Each of a team's books, by rank, as the count has left it. */
export function getBookTallies(team: Team, progress: TallyProgress): ReadonlyMap<MeldableRank, BookTally> {
    return new Map(selectMelds(team.melds, "books").map((book): [MeldableRank, BookTally] => [book.rank, {
        scoredAt: progress.bonuses.get(getBookKey(team.id, book.rank)) ?? null,
        left: book.cards.filter((card) => !progress.lifted.has(card.id)).length
    }]));
}
